import {
  diffHistoryUiDatesMillis,
  formatHistoryDateTimeLocal,
  formatHistoryUiDate,
  getDateToSave,
  getDayjs,
  isHistoryUiDateBefore,
  isNowAfterHistoryUiDate,
  shiftHistoryUiDate,
  US_LONG_AM_PM_UI
} from '@/solaris-lens-common-lib/src/utils/dates';

export { formatHistoryDateTimeLocal, formatHistoryUiDate };

export const DATE_RANGE_DIRECTION_BACK = -1;
export const DATE_RANGE_DIRECTION_FORWARD = 1;
export const FALLBACK_RANGE_MILLIS = 60 * 60 * 1000;

export const normalizeRangeMillis = (maxRangeMillis?: number): number => {
  if (typeof maxRangeMillis === 'number' && Number.isFinite(maxRangeMillis)) {
    const absoluteRange = Math.abs(maxRangeMillis);
    if (absoluteRange > 0) return absoluteRange;
  }
  return FALLBACK_RANGE_MILLIS;
};

export const getStartDateFromEnd = (
  endDate: string,
  maxRangeMillis?: number
): string => {
  const rangeMillis = normalizeRangeMillis(maxRangeMillis);
  return shiftHistoryUiDate(endDate, -rangeMillis);
};

export const getEndDateFromStart = (
  startDate: string,
  maxRangeMillis?: number
): string => {
  const rangeMillis = normalizeRangeMillis(maxRangeMillis);
  const candidate = shiftHistoryUiDate(startDate, rangeMillis);

  return isNowAfterHistoryUiDate(candidate)
    ? candidate
    : getDayjs().format(US_LONG_AM_PM_UI);
};

export const isStartBeforeEnd = (
  startDate: string,
  endDate: string
): boolean => isHistoryUiDateBefore(startDate, endDate);

export const ensureStartBeforeEnd = (
  startDate: string,
  endDate: string,
  maxRangeMillis?: number
): { startDate: string; endDate: string } => {
  if (isStartBeforeEnd(startDate, endDate)) {
    return { startDate, endDate };
  }

  return {
    startDate: getStartDateFromEnd(endDate, maxRangeMillis),
    endDate
  };
};

export const getFetchDateRange = (
  startDate: string,
  endDate: string,
  maxRangeMillis?: number
): { startDate: string; endDate: string } => {
  const validRange = ensureStartBeforeEnd(
    String(startDate),
    String(endDate),
    maxRangeMillis
  );

  return {
    startDate: getDateToSave(validRange.startDate),
    endDate: getDateToSave(validRange.endDate)
  };
};

export const getInitialDateRange = (
  maxRangeMillis?: number
): { startDate: string; endDate: string } => {
  const endDate = getDayjs().format(US_LONG_AM_PM_UI);
  return {
    startDate: getStartDateFromEnd(endDate, maxRangeMillis),
    endDate
  };
};

export const canNavigateDateRangeForward = (endDate: string): boolean =>
  isNowAfterHistoryUiDate(endDate);

export const getDateRangeDurationMillis = (
  startDate: string,
  endDate: string,
  fallbackRangeMillis?: number
): number => {
  const durationMillis = diffHistoryUiDatesMillis(startDate, endDate);

  if (durationMillis > 0) return durationMillis;
  return normalizeRangeMillis(fallbackRangeMillis);
};

export const getShiftedDateRange = (
  startDate: string,
  endDate: string,
  direction: number,
  fallbackRangeMillis?: number
): { startDate: string; endDate: string } | null => {
  if (
    direction === DATE_RANGE_DIRECTION_FORWARD &&
    !canNavigateDateRangeForward(endDate)
  ) {
    return null;
  }

  const rangeMillis = getDateRangeDurationMillis(
    startDate,
    endDate,
    fallbackRangeMillis
  );
  if (!rangeMillis) return null;

  const shiftMillis = rangeMillis * direction;
  const nextStart = shiftHistoryUiDate(startDate, shiftMillis);
  const nextEnd = shiftHistoryUiDate(endDate, shiftMillis);

  if (
    direction === DATE_RANGE_DIRECTION_FORWARD &&
    !isNowAfterHistoryUiDate(nextEnd)
  ) {
    const nowFormatted = getDayjs().format(US_LONG_AM_PM_UI);
    return {
      startDate: getStartDateFromEnd(nowFormatted, rangeMillis),
      endDate: nowFormatted
    };
  }

  return ensureStartBeforeEnd(nextStart, nextEnd, rangeMillis);
};
